import {
  ArrowRight,
  Ban,
  CalendarClock,
  Check,
  CircleUserRound,
  Cloud,
  CloudCog,
  CreditCard,
  ExternalLink,
  FileKey2,
  KeyRound,
  Laptop,
  ListFilter,
  LoaderCircle,
  LogOut,
  Menu,
  MoreHorizontal,
  Plus,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Trash2,
  Tv,
  UsersRound,
  UserRoundCheck,
  X
} from "lucide-react";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import type {
  AccountSummary,
  DashboardOverview,
  DeviceSummary,
  EntitlementSummary,
  ProfileSummary
} from "../shared/contracts";
import { api, ApiError, formatDate, timeAgo } from "./api";

interface SessionResponse {
  account: AccountSummary;
}

interface ConfigResponse {
  turnstileSiteKey: string;
  appOrigin: string;
}

type Loadable<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; value: T };

const FEATURES = [
  {
    icon: CloudCog,
    title: "Cloud playlist backup",
    text: "Recover profiles after a reinstall without typing long URLs again."
  },
  {
    icon: Laptop,
    title: "Manage from any browser",
    text: "Add and organize M3U or Xtream profiles from your phone or computer."
  },
  {
    icon: Tv,
    title: "Sync up to five TVs",
    text: "Share profiles or target a different lineup to each television."
  },
  {
    icon: ListFilter,
    title: "Advanced channel rules",
    text: "Reuse include and exclude rules across profiles and devices."
  },
  {
    icon: CalendarClock,
    title: "Device targeting",
    text: "Choose exactly which profiles and rules belong on each television."
  },
  {
    icon: RefreshCw,
    title: "Secure restore",
    text: "Restore Premium access and assigned profiles without retyping provider details."
  }
];

function buttonClass(kind: "primary" | "secondary" | "danger" = "primary") {
  return `button button-${kind}`;
}

function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div className="loading">
      <LoaderCircle className="spin" size={26} />
      <span>{label}</span>
    </div>
  );
}

function ErrorNotice({ message }: { message: string }) {
  return (
    <div className="notice notice-error" role="alert">
      {message}
    </div>
  );
}

function Brand({ compact = false }: { compact?: boolean }) {
  return (
    <a className="brand" href={compact ? "/app" : "/"}>
      <span className="brand-mark">
        <span />
      </span>
      <span>
        <strong>VELA</strong>
        {!compact && <small>IPTV, organized</small>}
      </span>
    </a>
  );
}

function PublicHeader({ account }: { account?: AccountSummary }) {
  return (
    <header className="public-header">
      <Brand />
      <nav>
        <a href="/#features">Premium features</a>
        <a href="/pair">Pair a TV</a>
        <a href={account ? "/app" : "/signin"} className={buttonClass("secondary")}>
          {account ? "Open dashboard" : "Sign in"}
        </a>
      </nav>
    </header>
  );
}

function Landing({ account }: { account?: AccountSummary }) {
  return (
    <div className="marketing-page">
      <PublicHeader account={account} />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <div className="eyebrow">
              <Sparkles size={16} /> Vela Premium
            </div>
            <h1>Your IPTV setup, available on every TV.</h1>
            <p>
              Back up playlists, manage them from a browser, target individual
              TVs, and restore everything by scanning one QR code.
            </p>
            <div className="hero-actions">
              <a className={buttonClass()} href={account ? "/app" : "/signin"}>
                {account ? "Open Vela Cloud" : "Get started"}
                <ArrowRight size={19} />
              </a>
              <a className={buttonClass("secondary")} href="/pair">
                Pair or restore a TV
              </a>
            </div>
            <div className="trust-row">
              <span><ShieldCheck size={17} /> Encrypted credentials</span>
              <span><Check size={17} /> Local playback stays free</span>
              <span><Tv size={17} /> Up to 5 TVs</span>
            </div>
          </div>
          <div className="hero-console" aria-label="Vela Cloud preview">
            <div className="console-bar">
              <span />
              <span />
              <span />
              <strong>Vela Cloud</strong>
            </div>
            <div className="console-content">
              <div className="console-side">
                <Brand compact />
                <span className="active">Overview</span>
                <span>Playlists</span>
                <span>Devices</span>
                <span>Rules</span>
              </div>
              <div className="console-main">
                <div className="mini-premium">
                  <Sparkles size={15} /> Premium active
                </div>
                <h3>Everything is in sync</h3>
                <div className="mini-stats">
                  <span><b>3</b> TVs</span>
                  <span><b>5</b> Playlists</span>
                  <span><b>12</b> Rules</span>
                </div>
                <div className="mini-device">
                  <Tv size={24} />
                  <span><b>Living room TV</b><small>Synced moments ago</small></span>
                  <Check size={20} />
                </div>
                <div className="mini-device">
                  <Tv size={24} />
                  <span><b>Bedroom TV</b><small>2 profiles assigned</small></span>
                  <Check size={20} />
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="feature-section" id="features">
          <div className="section-heading">
            <span>Useful beyond the player</span>
            <h2>Premium features that save real setup time.</h2>
            <p>
              Vela never sells or supplies channels. Premium is for secure
              management, synchronization, and automation.
            </p>
          </div>
          <div className="feature-grid">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <article key={title}>
                <Icon size={24} />
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>
      </main>
      <footer className="public-footer">
        <Brand compact />
        <p>Vela provides no media or IPTV service. Use only authorized sources.</p>
      </footer>
    </div>
  );
}

function SignIn({
  onSignedIn
}: {
  onSignedIn: (account: AccountSummary) => void;
}) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [config, setConfig] = useState<ConfigResponse>();

  useEffect(() => {
    api<ConfigResponse>("/api/config").then(setConfig).catch(() => undefined);
  }, []);

  async function requestCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/api/auth/request-code", {
        method: "POST",
        body: JSON.stringify({
          email,
          turnstileToken:
            !config?.turnstileSiteKey ||
            config.turnstileSiteKey.startsWith("REPLACE_")
              ? "dev-bypass"
              : (document.querySelector(
                  '[name="cf-turnstile-response"]'
                ) as HTMLInputElement | null)?.value
        })
      });
      setStep("code");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Try again.");
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const response = await api<SessionResponse>("/api/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ email, code })
      });
      onSignedIn(response.account);
      window.location.assign(
        new URLSearchParams(window.location.search).get("returnTo") || "/app"
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Try again.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (
      !config ||
      config.turnstileSiteKey.startsWith("REPLACE_") ||
      document.querySelector('script[src*="turnstile"]')
    ) {
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
    script.async = true;
    script.defer = true;
    document.head.append(script);
  }, [config]);

  return (
    <div className="auth-page">
      <a className="back-home" href="/">← Back to Vela</a>
      <section className="auth-card">
        <Brand />
        <div className="auth-icon"><KeyRound size={27} /></div>
        <h1>{step === "email" ? "Sign in to Vela Cloud" : "Check your email"}</h1>
        <p>
          {step === "email"
            ? "No password to remember. We’ll email a private six-digit code."
            : `Enter the code sent to ${email}. It expires in 10 minutes.`}
        </p>
        {error && <ErrorNotice message={error} />}
        {step === "email" ? (
          <form onSubmit={requestCode}>
            <label>
              Email address
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
            </label>
            {config &&
              !config.turnstileSiteKey.startsWith("REPLACE_") && (
                <div
                  className="cf-turnstile"
                  data-sitekey={config.turnstileSiteKey}
                  data-theme="dark"
                />
              )}
            <button className={buttonClass()} disabled={busy}>
              {busy ? <LoaderCircle className="spin" /> : "Email sign-in code"}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode}>
            <label>
              Six-digit code
              <input
                className="code-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="000000"
                required
                autoFocus
              />
            </label>
            <button className={buttonClass()} disabled={busy || code.length !== 6}>
              {busy ? <LoaderCircle className="spin" /> : "Continue"}
            </button>
            <button
              className="text-button"
              type="button"
              onClick={() => setStep("email")}
            >
              Use a different email
            </button>
          </form>
        )}
        <small>
          By continuing, you agree to Vela’s Terms and Privacy Policy.
        </small>
      </section>
    </div>
  );
}

const NAV = [
  { href: "/app", label: "Overview", icon: Cloud },
  { href: "/app/playlists", label: "Playlists", icon: FileKey2 },
  { href: "/app/devices", label: "Devices", icon: Tv },
  { href: "/app/rules", label: "Rules", icon: ListFilter },
  { href: "/app/schedules", label: "Schedules", icon: CalendarClock },
  { href: "/app/billing", label: "Premium", icon: Sparkles }
];

function AppShell({
  account,
  children
}: {
  account: AccountSummary;
  children: ReactNode;
}) {
  const path = window.location.pathname;
  const [mobile, setMobile] = useState(false);
  async function signOut() {
    await api("/api/auth/session", { method: "DELETE" });
    window.location.assign("/");
  }
  return (
    <div className="app-shell">
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <div className="sidebar-head">
          <Brand compact />
          <button className="icon-button mobile-only" onClick={() => setMobile(false)}>
            <X />
          </button>
        </div>
        <nav>
          {NAV.map(({ href, label, icon: Icon }) => (
            <a
              key={href}
              className={
                path === href ||
                (href !== "/app" && path.startsWith(`${href}/`))
                  ? "active"
                  : ""
              }
              href={href}
            >
              <Icon size={19} />
              {label}
            </a>
          ))}
        </nav>
        <div className="sidebar-bottom">
          <a href="/pair"><Smartphone size={19} /> Pair a TV</a>
          <button onClick={signOut}><LogOut size={19} /> Sign out</button>
          <span className="account-chip">
            <CircleUserRound size={30} />
            <span><b>{account.email}</b><small>Vela account</small></span>
          </span>
        </div>
      </aside>
      {mobile && <button className="scrim" onClick={() => setMobile(false)} />}
      <div className="app-main">
        <header className="app-header">
          <button className="icon-button mobile-only" onClick={() => setMobile(true)}>
            <Menu />
          </button>
          <span className="header-security">
            <ShieldCheck size={17} /> Credentials encrypted
          </span>
          <a className="pair-shortcut" href="/pair">
            <Plus size={17} /> Pair TV
          </a>
        </header>
        <main>{children}</main>
      </div>
    </div>
  );
}

function PageHeader({
  eyebrow,
  title,
  description,
  action
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        <span>{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  detail
}: {
  icon: typeof Tv;
  label: string;
  value: string | number;
  detail: string;
}) {
  return (
    <article className="stat-card">
      <span className="stat-icon"><Icon size={21} /></span>
      <span><small>{label}</small><strong>{value}</strong><em>{detail}</em></span>
    </article>
  );
}

function PremiumBanner({ entitlement }: { entitlement: EntitlementSummary }) {
  if (entitlement.premium) {
    return (
      <div className="premium-banner active">
        <span className="premium-orb"><Sparkles /></span>
        <span>
          <small>Vela Premium</small>
          <strong>Your cloud setup is protected and ready to sync.</strong>
        </span>
        <span className="status-pill"><Check size={15} /> Active</span>
        <a href="/app/billing">Manage <ArrowRight size={17} /></a>
      </div>
    );
  }
  return (
    <div className="premium-banner">
      <span className="premium-orb"><Sparkles /></span>
      <span>
        <small>Unlock Vela Premium</small>
        <strong>Back up profiles, manage from the web, and sync up to five TVs.</strong>
      </span>
      <a className={buttonClass()} href="/app/billing">View Premium</a>
    </div>
  );
}

function EmptyState({
  icon: Icon,
  title,
  text,
  action
}: {
  icon: typeof Tv;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <Icon size={30} />
      <h3>{title}</h3>
      <p>{text}</p>
      {action}
    </div>
  );
}

function Overview() {
  const [data, setData] = useState<Loadable<DashboardOverview>>({
    status: "loading"
  });
  useEffect(() => {
    api<DashboardOverview>("/api/dashboard")
      .then((value) => setData({ status: "ready", value }))
      .catch((error: Error) =>
        setData({ status: "error", message: error.message })
      );
  }, []);
  if (data.status === "loading") return <Spinner label="Loading your Vela setup" />;
  if (data.status === "error") return <ErrorNotice message={data.message} />;
  const value = data.value;
  return (
    <>
      <PageHeader
        eyebrow="Vela Cloud"
        title="Your cloud setup"
        description="Profiles, devices, and recent synchronization activity in one place."
        action={
          <a className={buttonClass()} href="/app/playlists">
            <Plus size={18} /> Add playlist
          </a>
        }
      />
      <PremiumBanner entitlement={value.entitlement} />
      <div className="stats-grid">
        <Stat icon={Tv} label="Paired TVs" value={value.devices.filter((d) => !d.revoked).length} detail={`of ${value.entitlement.deviceLimit} available`} />
        <Stat icon={FileKey2} label="Cloud profiles" value={value.profiles.length} detail="encrypted at rest" />
        <Stat icon={RefreshCw} label="Sync revision" value={value.revision} detail="latest cloud state" />
        <Stat icon={ShieldCheck} label="Protection" value="AES-256" detail="credential vault" />
      </div>
      <div className="dashboard-columns">
        <section className="panel">
          <div className="panel-title">
            <span><Tv size={19} /> Your TVs</span>
            <a href="/app/devices">View all</a>
          </div>
          {value.devices.length ? (
            <div className="row-list">
              {value.devices.slice(0, 4).map((device) => (
                <div className="data-row" key={device.id}>
                  <span className="row-icon"><Tv size={20} /></span>
                  <span className="row-main"><b>{device.name}</b><small>{device.platform}</small></span>
                  <span className="row-meta"><b>{device.profileCount} profiles</b><small>{timeAgo(device.lastSeenAt)}</small></span>
                  <span className={device.revoked ? "dot off" : "dot"} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Tv}
              title="No TVs paired yet"
              text="Open Vela on a TV and scan its Premium QR code."
              action={<a href="/pair" className={buttonClass("secondary")}>Pair TV</a>}
            />
          )}
        </section>
        <section className="panel">
          <div className="panel-title">
            <span><RefreshCw size={19} /> Recent activity</span>
          </div>
          {value.activity.length ? (
            <div className="activity-list">
              {value.activity.slice(0, 7).map((entry) => (
                <div key={entry.id}>
                  <span className="activity-dot" />
                  <span><b>{entry.action.replaceAll(".", " ")}</b><small>{timeAgo(entry.createdAt)}</small></span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon={RefreshCw} title="No cloud activity yet" text="Changes from this portal and your TVs will appear here." />
          )}
        </section>
      </div>
    </>
  );
}

function Modal({
  title,
  children,
  onClose
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="modal-layer" role="dialog" aria-modal="true">
      <button className="modal-scrim" onClick={onClose} aria-label="Close" />
      <section className="modal-card">
        <header><h2>{title}</h2><button className="icon-button" onClick={onClose}><X /></button></header>
        {children}
      </section>
    </div>
  );
}

function Playlists() {
  const [profiles, setProfiles] = useState<ProfileSummary[]>([]);
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [profilesResponse, devicesResponse] = await Promise.all([
        api<{ profiles: ProfileSummary[] }>("/api/profiles"),
        api<{ devices: DeviceSummary[] }>("/api/devices")
      ]);
      setProfiles(profilesResponse.profiles);
      setDevices(devicesResponse.devices.filter((device) => !device.revoked));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load profiles.");
    } finally {
      setLoading(false);
    }
  }, []);
  // The callback performs the page's initial remote-data load.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void load(); }, [load]);
  async function remove(id: string) {
    if (!window.confirm("Remove this cloud profile? TVs keep their last local copy until the next sync.")) return;
    await api(`/api/profiles/${encodeURIComponent(id)}`, { method: "DELETE" });
    await load();
  }
  return (
    <>
      <PageHeader
        eyebrow="Cloud library"
        title="Playlists"
        description="Credentials are encrypted before storage and are never shown again in this portal."
        action={<button className={buttonClass()} onClick={() => setShowAdd(true)}><Plus size={18} /> Add playlist</button>}
      />
      {error && <ErrorNotice message={error} />}
      <section className="panel">
        <div className="panel-title">
          <span><FileKey2 size={19} /> Cloud-managed profiles</span>
          <span className="count-pill">{profiles.length}</span>
        </div>
        {loading ? <Spinner /> : profiles.length ? (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Name</th><th>Type</th><th>TVs</th><th>Updated</th><th /></tr></thead>
              <tbody>
                {profiles.map((profile) => (
                  <tr key={profile.id}>
                    <td><span className="table-title"><span className="row-icon"><FileKey2 size={19} /></span><b>{profile.name}</b></span></td>
                    <td><span className="type-pill">{profile.type.toUpperCase()}</span></td>
                    <td>{profile.assignedDeviceCount}</td>
                    <td>{formatDate(profile.updatedAt)}</td>
                    <td><button className="icon-button danger" onClick={() => void remove(profile.id)} aria-label={`Delete ${profile.name}`}><Trash2 size={17} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon={FileKey2} title="No cloud playlists" text="Add a profile here, then assign it to one or more paired TVs." action={<button className={buttonClass()} onClick={() => setShowAdd(true)}>Add your first playlist</button>} />
        )}
      </section>
      <div className="security-callout"><ShieldCheck size={20} /><span><b>Credential privacy</b><small>Vela encrypts URLs, usernames, and passwords with AES-256-GCM. The owner dashboard only sees profile metadata.</small></span></div>
      {showAdd && (
        <ProfileForm
          devices={devices}
          onClose={() => setShowAdd(false)}
          onSaved={async () => { setShowAdd(false); await load(); }}
        />
      )}
    </>
  );
}

function ProfileForm({
  devices,
  onClose,
  onSaved
}: {
  devices: DeviceSummary[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [type, setType] = useState<"m3u" | "xtream">("m3u");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    const payload =
      type === "m3u"
        ? {
            type,
            playlistUrl: form.get("playlistUrl"),
            epgUrl: form.get("epgUrl") || undefined
          }
        : {
            type,
            serverUrl: form.get("serverUrl"),
            username: form.get("username"),
            password: form.get("password"),
            epgUrl: form.get("epgUrl") || undefined
          };
    try {
      await api("/api/profiles", {
        method: "POST",
        body: JSON.stringify({
          name: form.get("name"),
          payload,
          deviceIds: form.getAll("deviceIds")
        })
      });
      await onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save profile.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Add cloud playlist" onClose={onClose}>
      <form className="stack-form" onSubmit={submit}>
        {error && <ErrorNotice message={error} />}
        <div className="segmented">
          <button type="button" className={type === "m3u" ? "active" : ""} onClick={() => setType("m3u")}>M3U playlist</button>
          <button type="button" className={type === "xtream" ? "active" : ""} onClick={() => setType("xtream")}>Xtream login</button>
        </div>
        <label>Profile name<input name="name" required placeholder="Family TV" /></label>
        {type === "m3u" ? (
          <label>M3U playlist URL<input name="playlistUrl" type="url" required placeholder="https://provider.example/playlist.m3u" /></label>
        ) : (
          <div className="form-grid">
            <label className="full">Server URL<input name="serverUrl" type="url" required placeholder="https://provider.example" /></label>
            <label>Username<input name="username" required autoComplete="off" /></label>
            <label>Password<input name="password" type="password" required autoComplete="new-password" /></label>
          </div>
        )}
        <label>EPG URL <small>optional</small><input name="epgUrl" type="url" placeholder="https://provider.example/guide.xml.gz" /></label>
        <fieldset>
          <legend>Assign to TVs</legend>
          {devices.length ? devices.map((device) => (
            <label className="check-row" key={device.id}><input type="checkbox" name="deviceIds" value={device.id} /><Tv size={18} /> {device.name}</label>
          )) : <p className="muted">Pair a TV now or assign this profile later.</p>}
        </fieldset>
        <div className="form-actions">
          <button type="button" className={buttonClass("secondary")} onClick={onClose}>Cancel</button>
          <button className={buttonClass()} disabled={busy}>{busy ? <LoaderCircle className="spin" /> : "Save encrypted profile"}</button>
        </div>
      </form>
    </Modal>
  );
}

function Devices() {
  const [data, setData] = useState<Loadable<DeviceSummary[]>>({ status: "loading" });
  const load = useCallback(() => {
    setData({ status: "loading" });
    api<{ devices: DeviceSummary[] }>("/api/devices")
      .then((response) => setData({ status: "ready", value: response.devices }))
      .catch((error: Error) => setData({ status: "error", message: error.message }));
  }, []);
  // The callback performs the page's initial remote-data load.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(load, [load]);
  async function revoke(id: string) {
    if (!window.confirm("Unpair this TV? Its local profiles and playback continue, but cloud sync stops.")) return;
    await api(`/api/devices/${encodeURIComponent(id)}`, { method: "DELETE" });
    load();
  }
  return (
    <>
      <PageHeader eyebrow="Device targeting" title="TVs" description="Pair up to five televisions and decide which profiles each one receives." action={<a href="/pair" className={buttonClass()}><Plus size={18} /> Pair TV</a>} />
      {data.status === "loading" ? <Spinner /> : data.status === "error" ? <ErrorNotice message={data.message} /> : (
        <section className="device-grid">
          {data.value.map((device) => (
            <article className={device.revoked ? "device-card revoked" : "device-card"} key={device.id}>
              <div className="device-top"><span className="device-screen"><Tv size={28} /></span><button className="icon-button"><MoreHorizontal /></button></div>
              <h3>{device.name}</h3>
              <p>{device.platform}</p>
              <div className="device-details"><span><b>{device.profileCount}</b><small>profiles</small></span><span><b>{device.lastAppliedRevision}</b><small>revision</small></span></div>
              <div className="device-foot"><span><span className={device.revoked ? "dot off" : "dot"} /> {device.revoked ? "Unpaired" : `Seen ${timeAgo(device.lastSeenAt)}`}</span>{!device.revoked && <button className="text-button danger-text" onClick={() => void revoke(device.id)}>Unpair</button>}</div>
            </article>
          ))}
          <a className="device-card add-device" href="/pair"><Plus size={30} /><h3>Pair another TV</h3><p>Scan the Premium QR code shown in Vela.</p></a>
        </section>
      )}
    </>
  );
}

interface RuleRecord {
  id: string;
  name: string;
  mode: "include" | "exclude";
  match_type: string;
  keyword: string;
  category_scope?: string;
  enabled: boolean;
}

function Rules() {
  const [rules, setRules] = useState<RuleRecord[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(() => api<{ rules: RuleRecord[] }>("/api/rules").then((response) => setRules(response.rules)).catch((caught: Error) => setError(caught.message)), []);
  useEffect(() => { void load(); }, [load]);
  async function remove(id: string) {
    await api(`/api/rules/${encodeURIComponent(id)}`, { method: "DELETE" });
    await load();
  }
  return (
    <>
      <PageHeader eyebrow="Channel organization" title="Rules" description="Create reusable include or exclude filters. TV-local rules still work without Premium." action={<button className={buttonClass()} onClick={() => setShowAdd(true)}><Plus size={18} /> New rule</button>} />
      {error && <ErrorNotice message={error} />}
      <section className="panel">
        <div className="panel-title"><span><ListFilter size={19} /> Cloud rule set</span><span className="count-pill">{rules.length}</span></div>
        {rules.length ? <div className="row-list">{rules.map((rule) => (
          <div className="data-row rule-row" key={rule.id}>
            <span className={`rule-mode ${rule.mode}`}>{rule.mode}</span>
            <span className="row-main"><b>{rule.name}</b><small>{rule.match_type.replaceAll("_", " ")} “{rule.keyword}”{rule.category_scope ? ` in ${rule.category_scope}` : ""}</small></span>
            <span className={rule.enabled ? "status-pill" : "status-pill muted-pill"}>{rule.enabled ? "Enabled" : "Paused"}</span>
            <button className="icon-button danger" onClick={() => void remove(rule.id)}><Trash2 size={17} /></button>
          </div>
        ))}</div> : <EmptyState icon={ListFilter} title="No cloud rules" text="Build a reusable filter and sync it to assigned TVs." action={<button className={buttonClass()} onClick={() => setShowAdd(true)}>Create a rule</button>} />}
      </section>
      {showAdd && <RuleForm onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await load(); }} />}
    </>
  );
}

function RuleForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api("/api/rules", { method: "POST", body: JSON.stringify({
        name: form.get("name"), mode: form.get("mode"), matchType: form.get("matchType"),
        keyword: form.get("keyword"), categoryScope: form.get("categoryScope") || undefined
      }) });
      await onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save rule."); }
  }
  return <Modal title="Create channel rule" onClose={onClose}><form className="stack-form" onSubmit={submit}>
    {error && <ErrorNotice message={error} />}
    <label>Rule name<input name="name" required placeholder="Hide regional alternates" /></label>
    <div className="form-grid"><label>Action<select name="mode"><option value="exclude">Exclude matching</option><option value="include">Only include matching</option></select></label><label>Match<select name="matchType"><option value="contains">Contains</option><option value="starts_with">Starts with</option><option value="ends_with">Ends with</option><option value="exact">Exact text</option><option value="regex">Regular expression</option></select></label></div>
    <label>Keyword<input name="keyword" required placeholder="CA:" /></label>
    <label>Category scope <small>optional</small><input name="categoryScope" placeholder="United States" /></label>
    <div className="form-actions"><button type="button" className={buttonClass("secondary")} onClick={onClose}>Cancel</button><button className={buttonClass()}>Save rule</button></div>
  </form></Modal>;
}

interface ScheduleRecord {
  id: string;
  name: string;
  timezone: string;
  enabled: boolean;
  entries: unknown[];
  updatedAt: string;
}

function Schedules() {
  const [schedules, setSchedules] = useState<ScheduleRecord[]>([]);
  const [devices, setDevices] = useState<DeviceSummary[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try {
      const [scheduleResponse, deviceResponse] = await Promise.all([
        api<{ schedules: ScheduleRecord[] }>("/api/schedules"),
        api<{ devices: DeviceSummary[] }>("/api/devices")
      ]);
      setSchedules(scheduleResponse.schedules);
      setDevices(deviceResponse.devices.filter((device) => !device.revoked));
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not load schedules."); }
  }, []);
  // The callback performs the page's initial remote-data load.
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void load(); }, [load]);
  return (
    <>
      <PageHeader eyebrow="Playback automation" title="Schedules" description="Define a rotation, duration, and target TV. Schedules sync but execute locally on the television." action={<button className={buttonClass()} onClick={() => setShowAdd(true)}><Plus size={18} /> New schedule</button>} />
      {error && <ErrorNotice message={error} />}
      <section className="panel">
        <div className="panel-title"><span><CalendarClock size={19} /> Schedules</span><span className="count-pill">{schedules.length}</span></div>
        {schedules.length ? <div className="row-list">{schedules.map((schedule) => (
          <div className="data-row" key={schedule.id}><span className="row-icon"><CalendarClock size={20} /></span><span className="row-main"><b>{schedule.name}</b><small>{schedule.timezone} · {schedule.entries.length} entries</small></span><span className={schedule.enabled ? "status-pill" : "status-pill muted-pill"}>{schedule.enabled ? "Enabled" : "Paused"}</span></div>
        ))}</div> : <EmptyState icon={CalendarClock} title="No playback schedules" text="Schedule a channel or VOD rotation for a paired TV." action={<button className={buttonClass()} onClick={() => setShowAdd(true)}>Create a schedule</button>} />}
      </section>
      {showAdd && <ScheduleForm devices={devices} onClose={() => setShowAdd(false)} onSaved={async () => { setShowAdd(false); await load(); }} />}
    </>
  );
}

function ScheduleForm({ devices, onClose, onSaved }: { devices: DeviceSummary[]; onClose: () => void; onSaved: () => Promise<void> }) {
  const [error, setError] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      const entries = JSON.parse(String(form.get("entries") || "[]")) as unknown[];
      await api("/api/schedules", { method: "POST", body: JSON.stringify({
        name: form.get("name"), deviceId: form.get("deviceId") || undefined,
        timezone: form.get("timezone"), entries
      }) });
      await onSaved();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not save schedule."); }
  }
  return <Modal title="Create playback schedule" onClose={onClose}><form className="stack-form" onSubmit={submit}>
    {error && <ErrorNotice message={error} />}
    <label>Schedule name<input name="name" required placeholder="Morning news rotation" /></label>
    <div className="form-grid"><label>Target TV<select name="deviceId"><option value="">All paired TVs</option>{devices.map((device) => <option value={device.id} key={device.id}>{device.name}</option>)}</select></label><label>Timezone<input name="timezone" required defaultValue={Intl.DateTimeFormat().resolvedOptions().timeZone} /></label></div>
    <label>Entries (JSON)<textarea name="entries" rows={7} defaultValue={'[\n  {"start":"07:00","type":"channel","channelId":"example","minutes":60}\n]'} /></label>
    <p className="muted">A visual schedule editor should replace this structured entry box before public launch.</p>
    <div className="form-actions"><button type="button" className={buttonClass("secondary")} onClick={onClose}>Cancel</button><button className={buttonClass()}>Save schedule</button></div>
  </form></Modal>;
}

function Billing() {
  const [data, setData] = useState<Loadable<EntitlementSummary>>({ status: "loading" });
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    api<{ entitlement: EntitlementSummary }>("/api/billing").then((response) => setData({ status: "ready", value: response.entitlement })).catch((caught: Error) => setData({ status: "error", message: caught.message }));
  }, []);
  async function openBilling(kind: "checkout" | "portal", interval?: "monthly" | "annual") {
    setBusy(`${kind}-${interval || ""}`); setError("");
    try {
      const response = await api<{ url: string }>(`/api/billing/${kind}`, { method: "POST", body: JSON.stringify(interval ? { interval } : {}) });
      window.location.assign(response.url);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Billing is unavailable."); setBusy(""); }
  }
  if (data.status === "loading") return <Spinner label="Loading Premium" />;
  if (data.status === "error") return <ErrorNotice message={data.message} />;
  const entitlement = data.value;
  return (
    <>
      <PageHeader eyebrow="Vela Premium" title={entitlement.premium ? "Premium is active" : "A better way to manage every TV"} description={entitlement.premium ? "Your cloud features and device sync are enabled." : "Keep Vela’s local player free. Pay only for cloud management, synchronization, and automation."} />
      {error && <ErrorNotice message={error} />}
      <div className="billing-layout">
        <section className="price-card">
          <div className="price-head"><span className="premium-orb"><Sparkles /></span><span><small>Vela Premium</small><strong>{entitlement.premium ? entitlement.status.replaceAll("_", " ") : "Choose monthly or annual"}</strong></span>{entitlement.premium && <span className="status-pill"><Check size={15} /> Active</span>}</div>
          <div className="premium-feature-list">
            {FEATURES.map(({ icon: Icon, title }) => <span key={title}><Icon size={19} /><b>{title}</b><Check size={18} /></span>)}
          </div>
          {entitlement.premium ? (
            <>
              <p className="renewal">Renews or expires: <b>{formatDate(entitlement.renewsOrExpiresAt)}</b></p>
              {entitlement.source === "stripe" && <button className={buttonClass()} onClick={() => void openBilling("portal")} disabled={Boolean(busy)}>{busy ? <LoaderCircle className="spin" /> : <><CreditCard size={18} /> Manage subscription</>}</button>}
            </>
          ) : (
            <div className="plan-actions">
              <button className={buttonClass("secondary")} onClick={() => void openBilling("checkout", "monthly")} disabled={Boolean(busy)}><span><small>Monthly</small><b>Subscribe securely</b></span><ExternalLink size={18} /></button>
              <button className={buttonClass()} onClick={() => void openBilling("checkout", "annual")} disabled={Boolean(busy)}><span><small>Annual · best value</small><b>Subscribe securely</b></span><ExternalLink size={18} /></button>
            </div>
          )}
        </section>
        <aside className="billing-aside">
          <ShieldCheck size={30} />
          <h3>Your media remains yours</h3>
          <p>Vela provides no channels. Playlist credentials are encrypted and only delivered to TVs you explicitly pair.</p>
          <hr />
          <h3>If Premium expires</h3>
          <p>Cloud changes pause, but your last synchronized setup and all local playback remain usable.</p>
          <a href="/pair">Restore or link a purchase <ArrowRight size={16} /></a>
        </aside>
      </div>
    </>
  );
}

function PairPage({ account }: { account?: AccountSummary }) {
  const initial = new URLSearchParams(window.location.search).get("code") || "";
  const [code, setCode] = useState(initial.toUpperCase());
  const [details, setDetails] = useState<{ deviceName: string; platform: string; expiresAt: string }>();
  const [result, setResult] = useState<"approved" | "needs_purchase">();
  const [error, setError] = useState("");
  const normalized = code.trim().toUpperCase();
  useEffect(() => {
    if (!account || !/^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(normalized)) return;
    api<{ deviceName: string; platform: string; expiresAt: string }>(`/api/device-codes/${encodeURIComponent(normalized)}`).then(setDetails).catch((caught: Error) => setError(caught.message));
  }, [account, normalized]);
  async function approve() {
    if (!account) {
      window.location.assign(`/signin?returnTo=${encodeURIComponent(`/pair?code=${normalized}`)}`);
      return;
    }
    setError("");
    try {
      const response = await api<{ status: "approved" | "needs_purchase" }>("/api/device-codes/approve", { method: "POST", body: JSON.stringify({ userCode: normalized }) });
      setResult(response.status);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Could not pair this TV."); }
  }
  return (
    <div className="pair-page">
      <PublicHeader account={account} />
      <main className="pair-main">
        <section className="pair-card">
          <span className="pair-icon"><Tv size={34} /></span>
          <div className="eyebrow">Pair or restore</div>
          <h1>Link a Samsung TV</h1>
          <p>Enter the code shown on Vela’s Premium screen. Reinstalling Vela uses the same secure flow.</p>
          {error && <ErrorNotice message={error} />}
          {result === "approved" ? (
            <div className="pair-success"><Check size={30} /><h2>TV linked</h2><p>Return to your television. Vela will finish restoring your Premium access and assigned profiles.</p></div>
          ) : result === "needs_purchase" ? (
            <div className="pair-success premium-needed"><Sparkles size={30} /><h2>This account is on Free</h2><p>Start Premium, then return here and approve the same TV code before it expires.</p><a className={buttonClass()} href={`/app/billing?returnTo=${encodeURIComponent(`/pair?code=${normalized}`)}`}>View Premium</a></div>
          ) : (
            <>
              <label className="pair-code-label">TV code<input className="pair-code" value={code} onChange={(event) => setCode(event.target.value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 9).toUpperCase())} placeholder="ABCD-EFGH" autoFocus /></label>
              {details && <div className="pair-device"><Tv size={25} /><span><b>{details.deviceName}</b><small>{details.platform}</small></span><ShieldCheck size={21} /></div>}
              <button className={buttonClass()} onClick={() => void approve()} disabled={!/^[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(normalized)}>{account ? "Approve this TV" : "Sign in to continue"}<ArrowRight size={18} /></button>
            </>
          )}
          <small className="pair-note"><ShieldCheck size={15} /> Only approve a code visible on a TV you control.</small>
        </section>
      </main>
    </div>
  );
}

interface AdminData {
  summary: Record<string, number>;
  accounts: Array<Record<string, string | number | null>>;
  activity: Array<Record<string, string | null>>;
}

function AdminPage() {
  const [data, setData] = useState<Loadable<AdminData>>({ status: "loading" });
  const [busyAccount, setBusyAccount] = useState<string>();
  const [error, setError] = useState<string>();
  const load = useCallback(() => {
    return api<AdminData>("/api/admin/overview")
      .then((value) => setData({ status: "ready", value }))
      .catch((caught: Error) =>
        setData({ status: "error", message: caught.message })
      );
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const updateAccount = async (
    account: Record<string, string | number | null>,
    change:
      | { status: "active" | "disabled" }
      | { entitlement: "active" | "revoked"; entitlementUntil?: string | null }
  ) => {
    const accountId = String(account.id);
    setBusyAccount(accountId);
    setError(undefined);
    try {
      await api(`/api/admin/accounts/${encodeURIComponent(accountId)}`, {
        method: "PATCH",
        body: JSON.stringify(change)
      });
      await load();
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "The account was not updated."
      );
    } finally {
      setBusyAccount(undefined);
    }
  };
  if (data.status === "loading") return <div className="standalone-load"><Spinner label="Loading owner dashboard" /></div>;
  if (data.status === "error") return <div className="standalone-load"><ErrorNotice message={data.message} /></div>;
  return <div className="admin-page">
    <header><Brand /><span className="status-pill"><ShieldCheck size={15} /> Owner Access</span></header>
    <main><PageHeader eyebrow="Administration" title="Vela customers" description="Subscription, device, and activity metadata. Playlist credentials are never exposed here." />
      <div className="stats-grid"><Stat icon={UsersRound} label="Accounts" value={data.value.summary.accounts || 0} detail="active users" /><Stat icon={Sparkles} label="Premium" value={data.value.summary.premium || 0} detail="paid or grace" /><Stat icon={Tv} label="Paired TVs" value={data.value.summary.devices || 0} detail="not revoked" /><Stat icon={FileKey2} label="Profiles" value={data.value.summary.profiles || 0} detail="encrypted records" /></div>
      {error && <ErrorNotice message={error} />}
      <section className="panel"><div className="panel-title"><span><UsersRound size={19} /> Accounts</span></div><div className="table-wrap"><table><thead><tr><th>Email</th><th>Status</th><th>Entitlement</th><th>TVs</th><th>Profiles</th><th>Created</th><th>Owner actions</th></tr></thead><tbody>{data.value.accounts.map((account) => {
        const id = String(account.id);
        const disabled = String(account.status) === "disabled";
        const premium = ["active", "trial", "grace", "past_due"].includes(String(account.entitlement_status));
        return <tr key={id}><td><b>{String(account.email)}</b></td><td>{String(account.status)}</td><td>{String(account.entitlement_status || "free")}</td><td>{String(account.device_count)}</td><td>{String(account.profile_count)}</td><td>{formatDate(String(account.created_at))}</td><td><div className="admin-actions"><button className={buttonClass("secondary")} disabled={busyAccount === id} onClick={() => void updateAccount(account, { status: disabled ? "active" : "disabled" })}>{disabled ? <UserRoundCheck size={16} /> : <Ban size={16} />}{disabled ? "Enable" : "Disable"}</button><button className={buttonClass(premium ? "danger" : "primary")} disabled={busyAccount === id} onClick={() => void updateAccount(account, { entitlement: premium ? "revoked" : "active" })}>{premium ? <X size={16} /> : <Sparkles size={16} />}{premium ? "Revoke" : "Grant Premium"}</button></div></td></tr>;
      })}</tbody></table></div></section>
    </main>
  </div>;
}

function AuthenticatedRoute({
  account,
  children
}: {
  account?: AccountSummary;
  children: ReactNode;
}) {
  useEffect(() => {
    if (!account) {
      window.location.replace(`/signin?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    }
  }, [account]);
  if (!account) return <Spinner label="Opening sign in" />;
  return <AppShell account={account}>{children}</AppShell>;
}

export default function App() {
  const [session, setSession] = useState<Loadable<AccountSummary | undefined>>({
    status: "loading"
  });
  useEffect(() => {
    api<SessionResponse>("/api/auth/session")
      .then((response) => setSession({ status: "ready", value: response.account }))
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) {
          setSession({ status: "ready", value: undefined });
        } else {
          setSession({ status: "error", message: error instanceof Error ? error.message : "Could not start Vela Cloud." });
        }
      });
  }, []);
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const route = useMemo(() => {
    if (path === "/") return "landing";
    if (path === "/signin") return "signin";
    if (path === "/pair") return "pair";
    if (path === "/admin") return "admin";
    if (path === "/app/playlists") return "playlists";
    if (path === "/app/devices") return "devices";
    if (path === "/app/rules") return "rules";
    if (path === "/app/schedules") return "schedules";
    if (path === "/app/billing") return "billing";
    return "overview";
  }, [path]);
  if (session.status === "loading") return <div className="standalone-load"><Spinner label="Opening Vela Cloud" /></div>;
  if (session.status === "error") return <div className="standalone-load"><ErrorNotice message={session.message} /></div>;
  const account = session.value;
  if (route === "landing") return <Landing account={account} />;
  if (route === "signin") return account ? <Landing account={account} /> : <SignIn onSignedIn={(value) => setSession({ status: "ready", value })} />;
  if (route === "pair") return <PairPage account={account} />;
  if (route === "admin") return <AdminPage />;
  const page = route === "playlists" ? <Playlists /> : route === "devices" ? <Devices /> : route === "rules" ? <Rules /> : route === "schedules" ? <Schedules /> : route === "billing" ? <Billing /> : <Overview />;
  return <AuthenticatedRoute account={account}>{page}</AuthenticatedRoute>;
}
